import { createSlice } from '@reduxjs/toolkit'
import { ensureNumber } from 'apps/studio/store/reducers/entities/pdf'
import { loadFormData } from 'apps/formsCreationKit/store/actions/sharedActions'
import {
  addPdf,
  resetPdf,
  createElement,
  updateElement,
  deleteElement,
  replacePdf,
  initializePdfPages,
  createTable,
  moveTable,
  deleteTable,
  backFillPagesAndLayouts,
} from 'apps/studio/store/actions/sharedActions'
import { saveApp } from 'apps/formsCreationKit/store/reducers/settings/appSettings'
import { createDefaultPageData } from 'apps/studio/lib/utils/entities'

// const pageDefaults = {
//   id: null,
//   page_number: null,
//   section_ids: [],
//   styles: {}
// }

export const initialState = []

export const pagesSliceExtraReducers = builder => {
  builder
    .addCase(loadFormData, (state, action) => {
      const { pages } = action.payload
      return pages
    })
    .addCase(createElement, (state, action) => {
      const { page, section } = action.payload
      if (page && section) {
        return state.map(p =>
          p.id === page.id
            ? { ...p, section_ids: [...p.section_ids, section.id] }
            : p
        )
      }
      return state
    })
    .addCase(createTable, (state, action) => {
      const {
        page,
        section_id: outerSectionId,
        multiSectionGuid: innerSectionId,
      } = action.payload
      return state.map(p =>
        p.id === page.id
          ? {
              ...p,
              section_ids: [...p.section_ids, outerSectionId, innerSectionId],
            }
          : p
      )
    })
    .addCase(moveTable, (state, action) => {
      const { fromPage, toPage, outerSectionId, innerSectionId } =
        action.payload
      if (fromPage.id === toPage.id) return state
      const movedIds = [outerSectionId, innerSectionId].filter(Boolean)
      return state.map(p => {
        if (p.id === fromPage.id) {
          return {
            ...p,
            section_ids: p.section_ids.filter(id => !movedIds.includes(id)),
          }
        }
        if (p.id === toPage.id) {
          const additions = movedIds.filter(id => !p.section_ids.includes(id))
          return { ...p, section_ids: [...p.section_ids, ...additions] }
        }
        return p
      })
    })
    .addCase(updateElement, (state, action) => {
      const { page, section, sectionToDelete } = action.payload

      let updatedState = state

      if (section && page) {
        updatedState = updatedState.map(p =>
          p.id === page.id
            ? { ...p, section_ids: [...p.section_ids, section.id] }
            : p
        )
      }

      if (sectionToDelete) {
        updatedState = updatedState.map(p =>
          p.section_ids.includes(sectionToDelete)
            ? {
                ...p,
                section_ids: p.section_ids.filter(id => id !== sectionToDelete),
              }
            : p
        )
      }

      return updatedState
    })
    .addCase(deleteTable, (state, action) => {
      const { page, outerSection, innerSection } = action.payload
      const idsToRemove = new Set([outerSection.id, innerSection.id])
      return state.map(p =>
        p.id === page.id
          ? {
              ...p,
              section_ids: p.section_ids.filter(id => !idsToRemove.has(id)),
            }
          : p
      )
    })
    .addCase(deleteElement, (state, action) => {
      const { section, page } = action.payload

      if (section.id && page.id) {
        return state.map(p =>
          p.id === page.id
            ? {
                ...p,
                section_ids: p.section_ids.filter(id => id !== section.id),
              }
            : p
        )
      }

      return state
    })
    .addCase(addPdf, () => [])
    .addCase(initializePdfPages, (state, action) => {
      const { pageCount, pageGuids } = action.payload
      return Array.from({ length: pageCount }, (_, i) =>
        createDefaultPageData(pageGuids[i], i + 1)
      )
    })
    .addCase(backFillPagesAndLayouts, (state, action) => {
      const { missingPageNumbers, pageGuids } = action.payload
      const missingPages = Array.from(
        { length: missingPageNumbers.length },
        (_, i) => createDefaultPageData(pageGuids[i], missingPageNumbers[i])
      )
      return [...state, ...missingPages].sort(
        (a, b) => a.page_number - b.page_number
      )
    })
    .addCase(resetPdf, (state, action) =>
      // May need to revisit this but since we are only resetting the PDF to it's original state,
      // we need to keep the pages and clear out section associations and styles
      state.map(page => ({ ...page, section_ids: [], styles: {} }))
    )
    .addCase(replacePdf, (state, action) => {
      const { removedPageIds } = action.payload
      return state.filter(page => !removedPageIds.includes(page.id))
    })
    .addCase(saveApp.fulfilled, (state, action) => {
      const { pdf } = action.payload
      const { pages } = pdf
      return pages
    })
}

export const pagesSliceConfig = {
  name: 'pages',
  initialState: initialState,
  reducers: {
    updatePageStyles(state, action) {
      const { id, ...styles } = action.payload
      return state.map(page => {
        if (page.id === id) {
          return {
            ...page,
            styles: {
              ...page.styles,
              ...styles,
            },
          }
        }
        return page
      })
    },
    updatePagePosition(state, action) {
      const { id, ...position } = action.payload
      return state.map(page => {
        if (page.id === id) {
          return {
            ...page,
            position: {
              ...page.position,
              ...ensureNumber(position),
            },
          }
        }
        return page
      })
    },
  },
  extraReducers: pagesSliceExtraReducers,
}

export const pagesSlice = createSlice(pagesSliceConfig)
export const { actions: pagesActions, reducer: pagesReducer } = pagesSlice
